import {createFetcher, type GraphQLError} from '@udemy-v2/graphql';
import {
    ClpCourseDocument,
    useCoursesByIdQuery,
    type ClpCourseQuery,
    type ClpCourseQueryVariables,
} from '@/generated/graphql';
import {adaptCourse} from '@/app/lib/course/course-adapter';
import {getRuntimeConfig} from '@/app/api/config/get-config';
import {getHeaders} from '@/lib/gql-fetcher';
import {Course} from '@udemy-v2/browse-course';
import {
    CourseAccessError,
    toCourseAccessError,
} from '@/features/course-access/course-access-error';

/**
 * GraphQL fetcher for the course-by-landing-URL query. Uses the default
 * `ALLOW_PARTIAL` error policy so that sub-field errors on a nullable
 * field (e.g. a single review row failing) don't fail the whole query.
 *
 * For the `ACCESS_RESTRICTED` extension shape, the server returns
 * `{data: {courseByLandingURL: null}, errors: [{...}]}`. Under
 * ALLOW_PARTIAL the library spreads those errors onto the resolved
 * response at the top level (see `@udemy-v2/graphql` v2.36+), so we cast
 * the response to read `errors` and dispatch on the access-restriction
 * extension before treating the null `courseByLandingURL` as a hard miss.
 */
const courseFetcher = createFetcher({
    getUrl: () => {
        const fullGraphQLUrl = new URL(getRuntimeConfig('GRAPHQL_URL'), 'https://www.udemy.com');
        const isServer = typeof window === 'undefined';
        return isServer
            ? fullGraphQLUrl.toString()
            : `${window.location.origin}${fullGraphQLUrl.pathname}`;
    },
    getHeaders,
});

/**
 * Local cast helper: `createFetcher` v2.36+ returns `Promise<TData>` for
 * type compatibility with codegen consumers, but at runtime ALLOW_PARTIAL
 * spreads `errors` onto the response. This type captures the runtime
 * shape so we can read `response.errors` without leaking the cast into
 * every call site.
 */
type ClpCourseResponse = ClpCourseQuery & {errors?: GraphQLError[]};

export interface FetchCourseDataOptions {
    /** Forwarded to the `courseByLandingURL(password: …)` GraphQL argument. */
    password?: string;
    /** Forwarded to the `courseByLandingURL(inviteCode: …)` GraphQL argument. */
    inviteCode?: string;
}

/**
 * Fetches course data from GraphQL API and transforms it to our application's Course type.
 * @param courseLandingUrl - The landing URL of the course to fetch
 * @param headers - optional HTTP headers to include in the request
 * @param locale - The locale identifier for language preference (e.g., 'en_US', 'es_ES')
 * @param options - Optional access-control parameters (`password`, `inviteCode`)
 * @returns The transformed Course object
 * @throws CourseAccessError when the course exists but is access-restricted
 * @throws Error if the course cannot be fetched or transformed for any other reason
 */
export const fetchCourseData = async (
    courseLandingUrl: string,
    headers: Readonly<Headers>,
    locale?: string,
    options: FetchCourseDataOptions = {},
): Promise<Course> => {
    const headersObj = Object.fromEntries(headers.entries());

    // Add Accept-Language header if locale is provided
    if (locale) {
        // Convert locale format from 'en_US' to 'en-US' for HTTP header
        const acceptLanguage = locale.replace('_', '-');
        headersObj['Accept-Language'] = acceptLanguage;
    }
    try {
        if (process.env.DEBUG) {
            console.debug('Fetching course data for', courseLandingUrl);
        }
        const variables: ClpCourseQueryVariables = {
            landingURL: courseLandingUrl,
            password: options.password,
            inviteCode: options.inviteCode,
        };

        const response = (await courseFetcher<ClpCourseQuery, ClpCourseQueryVariables>(
            ClpCourseDocument,
            variables,
            new Headers(headersObj),
        )()) as ClpCourseResponse;

        // The `ACCESS_RESTRICTED` extension is the only top-level error we
        // map to a domain-typed throw — its presence nulls `courseByLandingURL`
        // and the caller needs the restriction details to pick a render path.
        // Other errors (sub-field nullables) are tolerated; the caller renders
        // whatever data did come back.
        const accessError = toCourseAccessError(response.errors);
        if (accessError) {
            throw accessError;
        }

        if (!response.courseByLandingURL) {
            throw new Error(`Unable to resolve course data with landing URL ${courseLandingUrl}`);
        }
        
        return adaptCourse(response.courseByLandingURL);
    } catch (error) {
        if (error instanceof CourseAccessError) {
            throw error;
        }
        console.warn('Error fetching course data:', error);
        throw error instanceof Error ? error : new Error('Failed to fetch course data');
    }
};

/**
 * Fetches course data from GraphQL API and transforms it to our application's Course type
 * @param id - The ID of the course to fetch
 * @param headers - optional HTTP headers to include in the request
 * @param locale - The locale identifier for language preference (e.g., 'en_US', 'es_ES')
 * @returns The transformed Course object
 * @throws Error if the course cannot be fetched or transformed
 */
export const fetchCourseDataByIds = async (
    ids: string[],
    headers?: Readonly<Headers>,
    locale?: string,
): Promise<Course[]> => {
    const headersObj = headers ? Object.fromEntries(headers.entries()) : {};

    // Add Accept-Language header if locale is provided
    if (locale) {
        // Convert locale format from 'en_US' to 'en-US' for HTTP header
        const acceptLanguage = locale.replace('_', '-');
        headersObj['Accept-Language'] = acceptLanguage;
    }
    try {
        if (process.env.DEBUG) {
            console.debug('Fetching course data by ids for', ids);
        }
        // Fetch course data from GraphQL API using the generated query fetcher
        const variables = {
            ids: ids,
            includeVariationAggregation: false,
        };

        const response = await useCoursesByIdQuery.fetcher(variables, new Headers(headersObj))();

        // Check if course data exists
        if (!response?.courses) {
            throw new Error(`Unable to resolve course data with ids ${ids}`);
        }
        const courses = response.courses;

        // Transform GraphQL course data to our application's Course type
        return courses.filter((course) => course !== null).map((course) => adaptCourse(course));
    } catch (error) {
        console.warn('Error fetching course data by id:', error);
        throw error instanceof Error ? error : new Error('Failed to fetch course data by id');
    }
};
